// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Simulators" on a machine's page in Settings › Connections › Agents (JP 2026-11-04): T3's own
// Agent device access switch, for people who only use Konvo. The machine keeps the setting; the bridge turns it
// on once by default, and after that it stays how its owner leaves it.

import {SwitchRow} from '@app/features/konvo_agents/components/AgentSettingsRows';
import {shareErrorMessage} from '@app/features/konvo_agents/components/AgentShareBits';
import Agents from '@app/features/konvo_agents/state/Agents';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import type {AgentMachineView} from '@konvo/schema/src/domains/konvo/Agents';
import {useLingui} from '@lingui/react/macro';
import {IconDeviceMobile} from '@tabler/icons-react';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

const deviceTool = (machine: AgentMachineView) => machine.tools.find((tool) => tool.can.deviceAccess === true);

/** Whether the card shows the row: the machine sends and its agent tool has the setting. */
export const showsDeviceAccessSetting = (machine: AgentMachineView) =>
	machine.limits.send && deviceTool(machine) !== undefined;

export const AgentMachineDeviceAccess = observer(({machine}: {machine: AgentMachineView}) => {
	const {i18n, t} = useLingui();
	const [saving, setSaving] = useState(false);
	const save = async (value: boolean) => {
		setSaving(true);
		try {
			await Agents.setDeviceAccess(machine.id, value);
		} catch (error) {
			ToastCommands.error(shareErrorMessage(i18n, error));
		} finally {
			setSaving(false);
		}
	};
	return (
		<SwitchRow
			icon={<IconDeviceMobile size={18} stroke={1.7} />}
			title={t({
				message: 'Simulators',
				comment: 'Agents Settings: switch that lets new agent chats open and control simulators on the machine.',
			})}
			description={t({
				message: 'iOS and Android, in new chats',
				comment: 'Agents Settings: what the Simulators switch covers. Chats already running are not affected.',
			})}
			value={deviceTool(machine)?.deviceAccess === true}
			disabled={saving || !machine.online}
			onChange={(value) => void save(value)}
			data-flx="konvo-agents.machine.device-access"
		/>
	);
});
